import { Component, DestroyRef, ElementRef, ViewChild, computed, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco';
import { AlertModule } from 'ngx-bootstrap/alert';
import { TypeaheadModule, TypeaheadMatch } from 'ngx-bootstrap/typeahead';
import { map, startWith } from 'rxjs';
import { Culture, CultureService, MediaService } from '@frontend/common';
import { AvatarPillComponent } from '@frontend/shared';
import { ProfessionalCulturesService } from './professional-cultures.service';
import { PRIMARY_CULTURES_MAX_SELECTED, OTHER_CULTURES_MAX_SELECTED } from './cultures-limits.type';

@Component({
  selector: 'multisite-professional-cultures',
  templateUrl: './professional-cultures.component.html',
  styleUrls: ['./professional-cultures.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, TranslocoModule, AlertModule, TypeaheadModule, AvatarPillComponent],
  providers: [provideTranslocoScope('profile')],
})
export class ProfessionalCulturesComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly cultureService = inject(CultureService);
  private readonly mediaService = inject(MediaService);
  readonly cloudinary_base_url = this.mediaService.cloudinary_base_url;
  readonly culturesService = inject(ProfessionalCulturesService);

  @ViewChild('primaryCultureSearchInput') private primaryCultureSearchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('otherCultureSearchInput') private otherCultureSearchInput?: ElementRef<HTMLInputElement>;

  readonly PRIMARY_CULTURES_MAX_SELECTED = PRIMARY_CULTURES_MAX_SELECTED;
  readonly OTHER_CULTURES_MAX_SELECTED = OTHER_CULTURES_MAX_SELECTED;

  readonly primaryCultureSearchControl = new FormControl<string>('', { nonNullable: true });
  readonly otherCultureSearchControl = new FormControl<string>('', { nonNullable: true });

  readonly primarySearchTerm = toSignal(this.primaryCultureSearchControl.valueChanges.pipe(startWith(this.primaryCultureSearchControl.value)), {
    initialValue: this.primaryCultureSearchControl.value,
  });

  readonly otherSearchTerm = toSignal(this.otherCultureSearchControl.valueChanges.pipe(startWith(this.otherCultureSearchControl.value)), {
    initialValue: this.otherCultureSearchControl.value,
  });

  readonly allCultures = toSignal(
    this.cultureService.cultures$.pipe(
      map((cultures) => cultures.filter((c) => c.category === 'geographic' && c.type === 'national')),
    ),
    { initialValue: [] as Culture[] },
  );
  readonly selectedPrimaryCultureIds = this.culturesService.selectedPrimaryCultureIds;
  readonly selectedOtherCultureIds = this.culturesService.selectedOtherCultureIds;
  readonly pendingCultureIds = this.culturesService.pendingCultureIds;
  readonly cultureLoading = computed(() => this.cultureService.loadingObject()['cultures'] === true);
  readonly loading = computed(() => this.culturesService.loading() || this.cultureLoading());
  readonly error = this.culturesService.error;

  readonly selectedPrimaryCultures = computed(() => this.mapCulturesToIds(this.selectedPrimaryCultureIds()));
  readonly selectedOtherCultures = computed(() => this.mapCulturesToIds(this.selectedOtherCultureIds()));

  readonly availableCultures = computed(() => {
    const selectedIds = new Set([...this.selectedPrimaryCultureIds(), ...this.selectedOtherCultureIds()]);
    return (this.allCultures() ?? []).filter((culture) => !selectedIds.has(culture.id));
  });

  readonly availableCulturesForPrimary = computed(() => this.filterCulturesByTerm(this.availableCultures(), this.primarySearchTerm()));
  readonly availableCulturesForOther = computed(() => this.filterCulturesByTerm(this.availableCultures(), this.otherSearchTerm()));

  ngOnInit(): void {
    this.requestCulturesIfNeeded();
    this.culturesService
      .loadSelection(false)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: () => void 0,
      });
  }

  private requestCulturesIfNeeded(): void {
    if (this.allCultures().length > 0 || this.cultureLoading()) {
      return;
    }

    this.cultureService
      .getCultures('geographic', 'national', false)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: () => void 0,
      });
  }

  isPending(cultureId: number): boolean {
    return this.pendingCultureIds().includes(cultureId);
  }

  onPrimarySelected(match: TypeaheadMatch): void {
    const culture = match.item as Culture;
    if (!culture) {
      return;
    }
    this.addToPrimary(culture);
    this.primaryCultureSearchControl.setValue('');
  }

  onOtherSelected(match: TypeaheadMatch): void {
    const culture = match.item as Culture;
    if (!culture) {
      return;
    }
    this.addToOther(culture);
    this.otherCultureSearchControl.setValue('');
  }

  addToPrimary(culture: Culture): void {
    this.culturesService
      .addToPrimary(culture.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: () => void 0,
      });
  }

  addToOther(culture: Culture): void {
    this.culturesService
      .addToOther(culture.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: () => void 0,
      });
  }

  removeFromPrimary(culture: Culture): void {
    this.culturesService
      .removeFromPrimary(culture.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: () => void 0,
      });
  }

  removeFromOther(culture: Culture): void {
    this.culturesService
      .removeFromOther(culture.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: () => void 0,
      });
  }

  trackById(_: number, culture: Culture): number {
    return culture.id;
  }

  private filterCulturesByTerm(cultures: Culture[], term: string): Culture[] {
    const cleanTerm = term?.trim().toLowerCase() ?? '';
    if (!cleanTerm.length) {
      return cultures;
    }

    return cultures.filter((culture) => {
      const haystack = [culture.name, culture.slug, culture.iso, ...(culture.metonyms ?? [])]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(cleanTerm);
    });
  }

  private mapCulturesToIds(cultureIds: number[]): Culture[] {
    const allCultures = this.allCultures() ?? [];
    const byId = new Map(allCultures.map((culture) => [culture.id, culture]));
    return cultureIds.map((cultureId) => byId.get(cultureId)).filter((culture): culture is Culture => culture !== undefined);
  }
}
